import React, { forwardRef, useState } from 'react'
import classNames from 'classnames'
import { Dropdown } from 'react-bootstrap'
import { type CrossClientQueryExecution } from '../../../models/CrossClient'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { Button, type ButtonProps } from '../../../components/Button/Button'
import { PortalDropdownMenu } from '../../Scheduling/shared/PortalDropdownMenu'
import { useDeleteOwnCrossClientExecution } from './hooks'
import { DeleteExecutionConfirmModal } from './DeleteExecutionConfirmModal'
import styles from './CrossClientExecutionActions.module.scss'

interface CrossClientExecutionActionsProps {
  execution: CrossClientQueryExecution
  onDeleted?: () => void
}

const KebabToggle = forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...rest }, ref) => (
  <Button ref={ref} {...rest} type='button' variant='borderlessGrayButton' className='p-2'>
    {children}
  </Button>
))
KebabToggle.displayName = 'KebabToggle'

export const CrossClientExecutionActions: React.FC<CrossClientExecutionActionsProps> = ({ execution, onDeleted }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { deleteOwnExecution, isDeleting } = useDeleteOwnCrossClientExecution()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

  const isOwnQuery = execution.createdBy?.id === currentUser.id

  if (!isOwnQuery) {
    return null
  }

  // The menu and modal render in portals, but React events still bubble through the tree to the clickable row. Clicks are
  // marked handled with preventDefault (rows skip those) rather than stopPropagation, which would stop open dropdowns'
  // document-level outside-click close from firing.
  return (
    <div onClick={(e) => e.preventDefault()} onKeyDown={(e) => e.stopPropagation()}>
      <Dropdown align='start'>
        <Dropdown.Toggle as={KebabToggle} aria-label='Query actions'>
          <i className='bi bi-three-dots zocks-text-gray-700' />
        </Dropdown.Toggle>
        <Dropdown.Menu
          as={PortalDropdownMenu}
          popperConfig={{ strategy: 'fixed' }}
          className={classNames(styles.menu, 'p-2 border zocks-border-color-gray-300 rounded-3')}
        >
          <Dropdown.Item className='d-flex align-items-center gap-3 p-2 paragraph-medium zocks-text-danger-dark' onClick={() => setShowDeleteConfirm(true)}>
            <i className='bi bi-trash' />
            Delete query
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <DeleteExecutionConfirmModal
        show={showDeleteConfirm}
        userQuestion={execution.userQuestion}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          const deleted = await deleteOwnExecution(execution)
          if (deleted) {
            setShowDeleteConfirm(false)
            onDeleted?.()
          }
        }}
        loading={isDeleting}
      />
    </div>
  )
}
